import React, { useState, useEffect, useRef } from 'react';
import { useAccount, useChainId, useSwitchChain, useWriteContract, useReadContracts, useWaitForTransactionReceipt } from 'wagmi';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { parseUnits, formatUnits } from 'viem';
import ErrorModal from './ErrorModal';
import { useWalletPortfolio } from '../context/WalletPortfolioContext';

const USDC_ADDRESS = '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359'; // Native USDC on Polygon
const POLYGON_CHAIN_ID = 137; // Markets + USDC are on Polygon mainnet; block trades on any other chain (e.g. Amoy testnet)
const USDC_DECIMALS = 6;
const MIN_TRADE_USDC = 1;
const MAX_TRADE_USDC = 10000;
const MAX_TRADE_UNITS = parseUnits(String(MAX_TRADE_USDC), USDC_DECIMALS);

/**
 * Approve enough for this trade and later ones on the same market, without
 * MaxUint256 (MetaMask's unlimited-spend scare copy). Cap at the site max
 * trade, or the user's current balance if that is smaller.
 */
function getBufferedApprovalAmount(tradeAmount, usdcBalance) {
  let buffered = MAX_TRADE_UNITS;
  if (typeof usdcBalance === 'bigint' && usdcBalance > 0n) {
    buffered = usdcBalance < MAX_TRADE_UNITS ? usdcBalance : MAX_TRADE_UNITS;
  }
  return buffered < tradeAmount ? tradeAmount : buffered;
}

function formatUsdcDisplay(units) {
  const n = Number(formatUnits(units, USDC_DECIMALS));
  if (!Number.isFinite(n)) return formatUnits(units, USDC_DECIMALS);
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}


// Minimal ABIs
const USDC_ABI = [
  {
    name: 'approve',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'spender', type: 'address' },
      { name: 'amount', type: 'uint256' }
    ],
    outputs: [{ type: 'bool' }]
  },
  {
    name: 'allowance',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: 'owner', type: 'address' },
      { name: 'spender', type: 'address' }
    ],
    outputs: [{ type: 'uint256' }]
  },
  {
    name: 'balanceOf',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ type: 'uint256' }]
  }
];

const MARKET_ABI = [
  {
    name: 'takePosition',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: '_outcome', type: 'uint8' },
      { name: '_amount', type: 'uint256' }
    ],
    outputs: []
  },
  {
    name: 'userPositions',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: 'user', type: 'address' },
      { name: 'outcome', type: 'uint8' }
    ],
    outputs: [{ type: 'uint256' }]
  },
  {
    name: 'totalBets',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: '_outcome', type: 'uint256' }],
    outputs: [{ type: 'uint256' }]
  },
  {
    name: 'totalPool',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }]
  }
];

function BettingPanel({ fixture, poolData, onTradeSuccess }) {
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();
  const { openConnectModal } = useConnectModal();
  const { refreshPortfolio } = useWalletPortfolio();
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [betAmount, setBetAmount] = useState('1');
  const [step, setStep] = useState('bet'); // 'bet', 'explain', 'approve', 'confirm'

  /** Prevents duplicate trade-success work (e.g. React Strict Mode, effect re-runs). */
  const tradeSuccessHandledHashRef = useRef(null);
  /** Tracks whether user manually selected an outcome (don't override with auto-select). */
  const userSelectedOutcomeRef = useRef(false);
  /** Trade + buffer-approve args captured when the in-app approval prompt opens. */
  const pendingTradeRef = useRef(null);
  
  // Error / success modal (same centered ErrorModal as claims — avoids wallet extension covering top-right toasts)
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: '', message: '', type: 'error' });
  
  // Check if trading is closed (kickoff time has passed)
  const isBettingClosed = () => {
    if (!fixture.kickoffTime) return false;
    const kickoffTimestamp = Number(fixture.kickoffTime);
    const now = Math.floor(Date.now() / 1000);
    return now >= kickoffTimestamp;
  };

  const is2way =
    fixture.marketFormat === '2way' ||
    (Array.isArray(fixture.allowedOutcomes) &&
      fixture.allowedOutcomes.length === 2 &&
      !fixture.allowedOutcomes.includes(1));

  const allowedOutcomeIds = Array.isArray(fixture.allowedOutcomes)
    ? fixture.allowedOutcomes
    : is2way
      ? [0, 2]
      : [0, 1, 2];

  const allOutcomes = [
    { id: 0, label: fixture.outcomeLabels?.[0] || fixture.homeTeam || 'Home Win', team: fixture.homeTeam },
    { id: 1, label: fixture.outcomeLabels?.[1] || 'Draw', team: 'Draw' },
    { id: 2, label: fixture.outcomeLabels?.[2] || fixture.awayTeam || 'Away Win', team: fixture.awayTeam },
  ];
  const outcomes = allOutcomes.filter((o) => allowedOutcomeIds.includes(o.id));
  const outcomeGridCols = outcomes.length === 2 ? '1fr 1fr' : '1fr 1fr 1fr';
  const homeLabelShort = is2way ? (fixture.homeTeam || 'Team 1') : 'Home';
  const awayLabelShort = is2way ? (fixture.awayTeam || 'Team 2') : 'Away';

  const { writeContract: approve, data: approveHash, error: approveError } = useWriteContract();
  const { writeContract: placeBet, data: betHash, error: betError } = useWriteContract();
  
  const { isLoading: isApproving, isSuccess: approveSuccess, error: approveTxError } = useWaitForTransactionReceipt({
    hash: approveHash,
  });
  
  const { isLoading: isBetting, isSuccess: betSuccess, error: betTxError } = useWaitForTransactionReceipt({
    hash: betHash,
  });
  
  // Handle errors
  useEffect(() => {
    if (approveError) {
      console.error('❌ Approval error:', approveError);
      setErrorModal({
        isOpen: true,
        title: 'Approval Failed',
        message: approveError.message || 'Unknown error',
        type: 'error'
      });
      setStep('bet');
    }
  }, [approveError]);
  
  useEffect(() => {
    if (betError) {
      console.error('❌ Trade placement error:', betError);
      setErrorModal({
        isOpen: true,
        title: 'Trade Failed',
        message: betError.message || 'Unknown error',
        type: 'error'
      });
      setStep('bet');
    }
  }, [betError]);
  
  useEffect(() => {
    if (approveTxError) {
      console.error('❌ Approval transaction error:', approveTxError);
      setErrorModal({
        isOpen: true,
        title: 'Approval Transaction Failed',
        message: approveTxError.message || 'Unknown error',
        type: 'error'
      });
      setStep('bet');
    }
  }, [approveTxError]);
  
  useEffect(() => {
    if (betTxError) {
      console.error('❌ Trade transaction error:', betTxError);
      setErrorModal({
        isOpen: true,
        title: 'Trade Transaction Failed',
        message: betTxError.message || 'Unknown error',
        type: 'error'
      });
      setStep('bet');
    }
  }, [betTxError]);

  const canReadMarket = isConnected && !!fixture.marketAddress && !!address;
  const { data: marketReads, refetch: refetchMarketReads } = useReadContracts({
    contracts: canReadMarket
      ? [
          {
            address: fixture.marketAddress,
            abi: MARKET_ABI,
            functionName: 'userPositions',
            args: [address, 0],
          },
          {
            address: fixture.marketAddress,
            abi: MARKET_ABI,
            functionName: 'userPositions',
            args: [address, 1],
          },
          {
            address: fixture.marketAddress,
            abi: MARKET_ABI,
            functionName: 'userPositions',
            args: [address, 2],
          },
          {
            address: fixture.marketAddress,
            abi: MARKET_ABI,
            functionName: 'totalBets',
            args: [0],
          },
          {
            address: fixture.marketAddress,
            abi: MARKET_ABI,
            functionName: 'totalBets',
            args: [1],
          },
          {
            address: fixture.marketAddress,
            abi: MARKET_ABI,
            functionName: 'totalBets',
            args: [2],
          },
          {
            address: fixture.marketAddress,
            abi: MARKET_ABI,
            functionName: 'totalPool',
          },
        ]
      : [],
    allowFailure: true,
    enabled: canReadMarket,
    staleTime: 0,
    gcTime: 60 * 1000,
  });

  const canReadUsdc = isConnected && !!address && !!fixture.marketAddress;
  const { data: usdcReads, refetch: refetchUsdcReads } = useReadContracts({
    contracts: canReadUsdc
      ? [
          {
            address: USDC_ADDRESS,
            abi: USDC_ABI,
            functionName: 'balanceOf',
            args: [address],
          },
          {
            address: USDC_ADDRESS,
            abi: USDC_ABI,
            functionName: 'allowance',
            args: [address, fixture.marketAddress],
          },
        ]
      : [],
    allowFailure: true,
    enabled: canReadUsdc,
    staleTime: 0,
    gcTime: 60 * 1000,
  });

  const homeBet = marketReads?.[0]?.result ?? 0n;
  const drawBet = marketReads?.[1]?.result ?? 0n;
  const awayBet = marketReads?.[2]?.result ?? 0n;
  const liveHomeBets = marketReads?.[3]?.result ?? 0n;
  const liveDrawBets = marketReads?.[4]?.result ?? 0n;
  const liveAwayBets = marketReads?.[5]?.result ?? 0n;
  const liveTotalPool = marketReads?.[6]?.result ?? 0n;
  const usdcBalance = usdcReads?.[0]?.result;
  const allowance = usdcReads?.[1]?.result;

  // Calculate expected payout for user's bets using live data from contract
  const calculateExpectedPayout = (outcome) => {
    const userBetAmount = outcome === 0 ? homeBet : outcome === 1 ? drawBet : awayBet;
    if (!userBetAmount || userBetAmount === 0n) return '0.00';
    
    // Use live pool data from contract if available, otherwise fallback to cached data
    const liveOutcomePool = outcome === 0 ? liveHomeBets : outcome === 1 ? liveDrawBets : liveAwayBets;
    const outcomePoolRaw = liveOutcomePool || (outcome === 0 ? safePoolData.homePool : outcome === 1 ? safePoolData.drawPool : safePoolData.awayPool);
    const totalPoolRaw = liveTotalPool || safePoolData.totalPool;
    
    // Convert to float
    const outcomePool = typeof outcomePoolRaw === 'bigint' 
      ? parseFloat(formatUnits(outcomePoolRaw, 6)) 
      : parseFloat(outcomePoolRaw);
    
    const totalPool = typeof totalPoolRaw === 'bigint'
      ? parseFloat(formatUnits(totalPoolRaw, 6))
      : parseFloat(totalPoolRaw);
    
    if (outcomePool === 0 || totalPool === 0) return '0.00';
    
    // User's share = (userBet / outcomePool) * totalPool * 0.95 (5% fee)
    const userShare = (parseFloat(formatUnits(userBetAmount, 6)) / outcomePool) * totalPool * 0.95;
    return userShare.toFixed(2);
  };

  const handleBet = async () => {
    if (!isConnected) {
      setErrorModal({
        isOpen: true,
        title: 'Wallet Not Connected',
        message: 'Please connect your wallet first',
        type: 'warning'
      });
      return;
    }

    // Block trades unless the wallet is on Polygon mainnet. The contract + USDC addresses are
    // mainnet-only, but Amoy testnet is a supported chain in wagmi — without this a user on Amoy
    // would send approve/takePosition against mainnet addresses on the wrong network.
    if (chainId !== POLYGON_CHAIN_ID) {
      setErrorModal({
        isOpen: true,
        title: 'Wrong Network',
        message: 'Please switch your wallet to the Polygon network to trade.',
        type: 'warning'
      });
      try {
        switchChain?.({ chainId: POLYGON_CHAIN_ID });
      } catch (_) {
        /* user can switch manually */
      }
      return;
    }

    if (selectedOutcome === null) {
      setErrorModal({
        isOpen: true,
        title: 'No Outcome Selected',
        message: is2way
          ? 'Please select a team to win (including extra time and penalties if needed)'
          : 'Please select an outcome (Home Win, Draw, or Away Win)',
        type: 'warning'
      });
      return;
    }

    if (!allowedOutcomeIds.includes(selectedOutcome)) {
      setErrorModal({
        isOpen: true,
        title: 'Invalid Outcome',
        message: 'This outcome is not available for this market.',
        type: 'warning'
      });
      return;
    }
    
    const amount = parseFloat(betAmount);
    if (!betAmount || isNaN(amount)) {
      setErrorModal({
        isOpen: true,
        title: 'Invalid Amount',
        message: 'Please enter a valid trade size',
        type: 'warning'
      });
      return;
    }
    
    if (amount < MIN_TRADE_USDC) {
      setErrorModal({
        isOpen: true,
        title: 'Amount Too Small',
        message: `Minimum trade size is ${MIN_TRADE_USDC} USDC`,
        type: 'warning'
      });
      return;
    }
    
    if (amount > MAX_TRADE_USDC) {
      setErrorModal({
        isOpen: true,
        title: 'Amount Too Large',
        message: `Maximum trade size is ${MAX_TRADE_USDC.toLocaleString()} USDC`,
        type: 'warning'
      });
      return;
    }
    
    if (!fixture.marketAddress) {
      setErrorModal({
        isOpen: true,
        title: 'Market Not Found',
        message: 'Market contract not found for this fixture',
        type: 'error'
      });
      return;
    }
    
    try {
      const amount = parseUnits(betAmount, USDC_DECIMALS);
      
      // Ensure market address is a valid string (not object)
      const marketAddress = typeof fixture.marketAddress === 'string' 
        ? fixture.marketAddress 
        : String(fixture.marketAddress);

      pendingTradeRef.current = {
        marketAddress,
        tradeAmount: amount,
        selectedOutcome
      };
      
      // Check if approval needed
      if (!allowance || allowance < amount) {
        const approvalAmount = getBufferedApprovalAmount(amount, usdcBalance);
        pendingTradeRef.current.approvalAmount = approvalAmount;
        const capLabel = formatUsdcDisplay(approvalAmount);
        setStep('explain');
        setErrorModal({
          isOpen: true,
          title: 'Allow USDC for this market',
          message: 'Your wallet will ask you to set a spending cap. That warning is MetaMask\'s default for any token approval — not a sign this trade is unsafe.',
          detail: `We'll request a cap of ${capLabel} USDC for this match only. That covers this trade and later trades on the same market, so you won't get this prompt every time.\n\nYou still confirm each trade, and you only spend the amount you enter. You can revoke this in MetaMask at any time.`,
          type: 'info',
          confirmLabel: 'Continue to wallet',
          cancelLabel: 'Cancel',
          onConfirm: () => {
            const pending = pendingTradeRef.current;
            if (!pending?.marketAddress || pending.approvalAmount == null) return;
            setStep('approve');
            approve({
              address: USDC_ADDRESS,
              abi: USDC_ABI,
              functionName: 'approve',
              args: [pending.marketAddress, pending.approvalAmount],
              gas: 100000n, // Explicit gas limit for approve
            });
          },
          onCloseCallback: () => {
            setStep((current) => {
              if (current === 'explain') {
                pendingTradeRef.current = null;
                return 'bet';
              }
              return current;
            });
          }
        });
      } else {
        setStep('confirm');
        placeBet({
          address: marketAddress,
          abi: MARKET_ABI,
          functionName: 'takePosition',
          args: [selectedOutcome, amount],
          gas: 300000n, // Explicit gas limit for bet placement
        });
      }
    } catch (error) {
      console.error('❌ Error while executing trade:', error);
      setErrorModal({
        isOpen: true,
        title: 'Transaction Error',
        message: error.message,
        type: 'error'
      });
      setStep('bet');
    }
  };

  useEffect(() => {
    if (approveSuccess && step === 'approve') {
      const pending = pendingTradeRef.current;
      const amount = pending?.tradeAmount ?? parseUnits(betAmount, USDC_DECIMALS);
      const outcome = pending?.selectedOutcome ?? selectedOutcome;
      const marketAddress = pending?.marketAddress
        ?? (typeof fixture.marketAddress === 'string'
          ? fixture.marketAddress
          : String(fixture.marketAddress));
      refetchUsdcReads();
      setStep('confirm');
      placeBet({
        address: marketAddress,
        abi: MARKET_ABI,
        functionName: 'takePosition',
        args: [outcome, amount],
        gas: 300000n, // Explicit gas limit
      });
    }
  }, [approveSuccess, step, betAmount, selectedOutcome, fixture.marketAddress, placeBet, refetchUsdcReads]);

  useEffect(() => {
    if (!betSuccess || !betHash) return;
    if (tradeSuccessHandledHashRef.current === betHash) return;
    tradeSuccessHandledHashRef.current = betHash;

    setStep('bet');
    setSelectedOutcome(null);
    setBetAmount('1');
    pendingTradeRef.current = null;
    refetchMarketReads();
    refetchUsdcReads();
    refreshPortfolio();
    [3000, 10000, 25000].forEach((ms) => {
      setTimeout(() => refreshPortfolio(), ms);
    });
    setErrorModal({
      isOpen: true,
      title: 'Trade Successful',
      message:
        'Your trade was confirmed on-chain. Your position and balances will update in a moment.',
      type: 'success',
      linkUrl: `https://polygonscan.com/tx/${betHash}`,
      linkLabel: 'View this transaction on PolygonScan',
      // Run after user dismisses modal — avoids remount (e.g. fetchFixtures) closing the dialog early
      onCloseCallback: () => {
        refetchMarketReads();
        refetchUsdcReads();
        refreshPortfolio();
        if (onTradeSuccess) {
          onTradeSuccess();
        }
      }
    });
  }, [
    betSuccess,
    betHash,
    onTradeSuccess,
    refetchMarketReads,
    refetchUsdcReads,
    refreshPortfolio
  ]);

  // Check if trading is closed (show this for both connected and not connected users)
  const bettingClosed = isBettingClosed();
  
  // Provide default poolData if null (will be fetched from contract when user connects)
  const safePoolData = poolData || {
    totalPool: '0',
    homePool: '0',
    drawPool: '0',
    awayPool: '0'
  };
  
  // Calculate odds for display (used in multiple places)
  const totalPool = parseFloat(safePoolData.totalPool || '0');
  const homePool = parseFloat(safePoolData.homePool || '0');
  const drawPool = parseFloat(safePoolData.drawPool || '0');
  const awayPool = parseFloat(safePoolData.awayPool || '0');
  
  const calculateOddsForDisplay = (outcomePool, returnNullIfZero = false) => {
    if (totalPool === 0) return returnNullIfZero ? null : '10.00';
    if (outcomePool === 0) return returnNullIfZero ? null : '10.00';
    return ((totalPool * 0.95) / outcomePool).toFixed(2);
  };
  
  const homeOdds = calculateOddsForDisplay(homePool);
  const drawOdds = calculateOddsForDisplay(drawPool);
  const awayOdds = calculateOddsForDisplay(awayPool);
  
  // For closed markets, show null for zero-pool outcomes
  const homeOddsClosed = calculateOddsForDisplay(homePool, true);
  const drawOddsClosed = calculateOddsForDisplay(drawPool, true);
  const awayOddsClosed = calculateOddsForDisplay(awayPool, true);

  // Auto-select the largest pool outcome on load for better UX.
  // This only runs until the user manually picks an outcome.
  useEffect(() => {
    // Reset manual-selection guard when market changes.
    userSelectedOutcomeRef.current = false;
    setSelectedOutcome(null);
  }, [fixture?.marketAddress]);

  useEffect(() => {
    if (selectedOutcome !== null || userSelectedOutcomeRef.current) {
      return;
    }

    const poolByOutcome = [
      Number(liveHomeBets > 0n ? formatUnits(liveHomeBets, 6) : (safePoolData.homePool || 0)),
      Number(liveDrawBets > 0n ? formatUnits(liveDrawBets, 6) : (safePoolData.drawPool || 0)),
      Number(liveAwayBets > 0n ? formatUnits(liveAwayBets, 6) : (safePoolData.awayPool || 0)),
    ];

    let bestOutcome = allowedOutcomeIds[0];
    let bestValue = poolByOutcome[bestOutcome] ?? 0;
    for (let i = 1; i < allowedOutcomeIds.length; i++) {
      const id = allowedOutcomeIds[i];
      const v = poolByOutcome[id] ?? 0;
      if (v > bestValue) {
        bestOutcome = id;
        bestValue = v;
      }
    }

    setSelectedOutcome(bestOutcome);
  }, [
    selectedOutcome,
    liveHomeBets,
    liveDrawBets,
    liveAwayBets,
    safePoolData.homePool,
    safePoolData.drawPool,
    safePoolData.awayPool
  ]);
  
  // Show trading closed message if game has started
  if (bettingClosed) {
    return (
      <div style={{
        marginTop: '10px',
        padding: '20px',
        background: 'linear-gradient(135deg, #ff6ba5 0%, #d6005a 100%)',
        borderRadius: '12px',
        color: 'white',
        textAlign: 'center',
        boxShadow: '0 4px 15px rgba(255, 0, 110, 0.3)'
      }}>
        <div style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '8px' }}>
          Trading Closed
        </div>
        <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '16px' }}>
          The match has started. No new trades can be opened.
        </div>
        
        {/* Final Odds */}
        <div style={{ 
          background: 'rgba(255, 255, 255, 0.15)',
          borderRadius: '8px',
          padding: '12px',
          marginTop: '16px'
        }}>
          <div style={{ fontSize: '11px', opacity: 0.9, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Final Odds
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: outcomeGridCols, gap: '8px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '10px', opacity: 0.8, marginBottom: '2px' }}>Home</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                {homeOddsClosed ? `${homeOddsClosed}x` : '—'}
              </div>
              {homePool > 0 && (
                <div style={{ fontSize: '9px', opacity: 0.7, marginTop: '2px' }}>${homePool.toFixed(0)}</div>
              )}
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '10px', opacity: 0.8, marginBottom: '2px' }}>Draw</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                {drawOddsClosed ? `${drawOddsClosed}x` : '—'}
              </div>
              {drawPool > 0 && (
                <div style={{ fontSize: '9px', opacity: 0.7, marginTop: '2px' }}>${drawPool.toFixed(0)}</div>
              )}
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '10px', opacity: 0.8, marginBottom: '2px' }}>Away</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                {awayOddsClosed ? `${awayOddsClosed}x` : '—'}
              </div>
              {awayPool > 0 && (
                <div style={{ fontSize: '9px', opacity: 0.7, marginTop: '2px' }}>${awayPool.toFixed(0)}</div>
              )}
            </div>
          </div>
        </div>
        
        <div style={{ 
          marginTop: '16px',
          fontSize: '12px',
          opacity: 0.8
        }}>
          Total Pool: ${totalPool.toFixed(2)} USDC
        </div>
        
        {isConnected && (homeBet > 0n || drawBet > 0n || awayBet > 0n) && (
          <div style={{ 
            marginTop: '12px',
            padding: '8px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '6px',
            fontSize: '11px',
            opacity: 0.9
          }}>
            You can claim winnings after the match is resolved
          </div>
        )}
      </div>
    );
  }
  
  if (!isConnected) {
    return (
      <div style={{
        marginTop: '10px',
        padding: '16px',
        background: 'linear-gradient(135deg, #ff6ba5 0%, #d6005a 100%)',
        borderRadius: '12px',
        color: 'white',
        boxShadow: '0 4px 15px rgba(255, 0, 110, 0.3)'
      }}>
        {/* Market Info */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>
              Total Pool
          </div>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
            ${totalPool.toFixed(2)} USDC
          </div>
        </div>
        
        {/* Current Odds */}
        <div style={{ 
          background: 'rgba(255, 255, 255, 0.15)',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '16px'
        }}>
          <div style={{ fontSize: '11px', opacity: 0.9, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Current Odds
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: outcomeGridCols, gap: '8px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '10px', opacity: 0.8, marginBottom: '2px' }}>Home</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                {homeOdds === '10.00' ? '—' : `${homeOdds}x`}
              </div>
              <div style={{ fontSize: '9px', opacity: 0.7, marginTop: '2px' }}>${homePool.toFixed(0)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '10px', opacity: 0.8, marginBottom: '2px' }}>Draw</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                {drawOdds === '10.00' ? '—' : `${drawOdds}x`}
              </div>
              <div style={{ fontSize: '9px', opacity: 0.7, marginTop: '2px' }}>${drawPool.toFixed(0)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '10px', opacity: 0.8, marginBottom: '2px' }}>Away</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                {awayOdds === '10.00' ? '—' : `${awayOdds}x`}
              </div>
              <div style={{ fontSize: '9px', opacity: 0.7, marginTop: '2px' }}>${awayPool.toFixed(0)}</div>
            </div>
          </div>
        </div>
        
        {/* Connect CTA - Clickable */}
        <button
          onClick={openConnectModal}
          style={{ 
            width: '100%',
            textAlign: 'center',
            padding: '12px',
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '8px',
            border: '2px dashed rgba(255, 255, 255, 0.4)',
            cursor: 'pointer',
            transition: 'all 0.2s',
            color: 'inherit',
            fontFamily: 'inherit'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.6)';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }}>
            🔗 Connect Wallet to Trade
          </div>
          <div style={{ fontSize: '11px', opacity: 0.9 }}>
            Start trading with as little as $1 USDC
          </div>
        </button>
      </div>
    );
  }

  // Check if user has any trades
  const hasHomeBet = homeBet && homeBet > 0n;
  const hasDrawBet = drawBet && drawBet > 0n;
  const hasAwayBet = awayBet && awayBet > 0n;
  const hasAnyBets = hasHomeBet || hasDrawBet || hasAwayBet;

  // Calculate net exposure (only if user has trades on multiple outcomes)
  const calculateNetExposure = () => {
    if (!hasAnyBets) return null;
    
    const homePayout = hasHomeBet ? parseFloat(calculateExpectedPayout(0)) : 0;
    const drawPayout = hasDrawBet ? parseFloat(calculateExpectedPayout(1)) : 0;
    const awayPayout = hasAwayBet ? parseFloat(calculateExpectedPayout(2)) : 0;
    
    const totalBet = 
      (hasHomeBet ? parseFloat(formatUnits(homeBet, 6)) : 0) +
      (hasDrawBet ? parseFloat(formatUnits(drawBet, 6)) : 0) +
      (hasAwayBet ? parseFloat(formatUnits(awayBet, 6)) : 0);
    
    // If only one trade, exposure is the full amount (no hedge)
    const betCount = [hasHomeBet, hasDrawBet, hasAwayBet].filter(Boolean).length;
    if (betCount === 1) {
      return { totalBet, exposure: totalBet, isHedged: false };
    }
    
    // If multiple trades, worst case is the minimum payout across all outcomes
    // If an outcome has no trade (0), that outcome returns 0 if it wins
    const payouts = [
      hasHomeBet ? homePayout : 0,
      hasDrawBet ? drawPayout : 0,
      hasAwayBet ? awayPayout : 0
    ];
    
    const worstCasePayout = Math.min(...payouts);
    const exposure = totalBet - worstCasePayout;
    
    return { totalBet, exposure, worstCasePayout, isHedged: true };
  };

  const netExposure = calculateNetExposure();

  return (
    <div style={{ marginTop: '10px', position: 'relative' }}>
      {/* Show User's Current Trades */}
      {hasAnyBets && (
        <div style={{
          padding: '12px',
          background: '#fff',
          border: '2px solid #e0e0e0',
          borderRadius: '8px',
          marginBottom: '10px'
        }}>
          <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '8px' }}>
            Your Trades
          </div>
          {hasHomeBet && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
              <span style={{ color: '#666' }}>Home: {formatUnits(homeBet, 6)} USDC</span>
              <strong style={{ color: '#2e7d32' }}>
                → {(parseFloat(calculateExpectedPayout(0)) - parseFloat(formatUnits(homeBet, 6))).toFixed(2)} USDC profit
              </strong>
            </div>
          )}
          {hasDrawBet && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
              <span style={{ color: '#666' }}>Draw: {formatUnits(drawBet, 6)} USDC</span>
              <strong style={{ color: '#2e7d32' }}>
                → {(parseFloat(calculateExpectedPayout(1)) - parseFloat(formatUnits(drawBet, 6))).toFixed(2)} USDC profit
              </strong>
            </div>
          )}
          {hasAwayBet && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: hasAwayBet && netExposure ? '8px' : '0' }}>
              <span style={{ color: '#666' }}>Away: {formatUnits(awayBet, 6)} USDC</span>
              <strong style={{ color: '#2e7d32' }}>
                → {(parseFloat(calculateExpectedPayout(2)) - parseFloat(formatUnits(awayBet, 6))).toFixed(2)} USDC profit
              </strong>
            </div>
          )}
          
          {/* Net Exposure - only show if user has multiple trades */}
          {netExposure && netExposure.isHedged && (
            <div style={{ 
              marginTop: '8px', 
              paddingTop: '8px', 
              borderTop: '1px solid #e0e0e0',
              fontSize: '12px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: '#666' }}>
                <span>Total trade size: {netExposure.totalBet.toFixed(2)} USDC</span>
                <span>Worst case: {netExposure.worstCasePayout.toFixed(2)} USDC</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                <span style={{ color: netExposure.exposure > 0 ? '#d32f2f' : '#2e7d32' }}>
                  Net exposure: {netExposure.exposure.toFixed(2)} USDC
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Place New Trade */}
      <div style={{
        padding: '12px',
        background: '#fff',
        border: '2px solid #e0e0e0',
        borderRadius: '8px'
      }}>
                
        {selectedOutcome === null && (
          <div style={{ fontSize: '12px', color: '#ff9800', marginBottom: '8px', textAlign: 'center' }}>
             Select an outcome to trade
          </div>
        )}

        {/* Outcome Selection */}
        <div style={{ display: 'grid', gridTemplateColumns: outcomeGridCols, gap: '8px', marginBottom: '12px' }}>
          {outcomes.map(outcome => {
            // Use live pool data if available
            const livePool = outcome.id === 0 ? liveHomeBets : outcome.id === 1 ? liveDrawBets : liveAwayBets;
            const cachedPool = outcome.id === 0 ? safePoolData.homePool : outcome.id === 1 ? safePoolData.drawPool : safePoolData.awayPool;
            const pool = livePool ? formatUnits(livePool, 6) : cachedPool;
            return (
              <button
                key={outcome.id}
                onClick={() => {
                  userSelectedOutcomeRef.current = true;
                  setSelectedOutcome(outcome.id);
                }}
                onMouseEnter={(e) => {
                  if (selectedOutcome !== outcome.id) {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #ff6ba5 0%, #d6005a 100%)';
                    e.currentTarget.style.opacity = '0.8';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedOutcome !== outcome.id) {
                    e.currentTarget.style.background = '#f5f5f5';
                    e.currentTarget.style.opacity = '1';
                  }
                }}
                style={{
                  padding: '12px 8px',
                  background: selectedOutcome === outcome.id ? 'linear-gradient(135deg, #ff6ba5 0%, #d6005a 100%)' : '#f5f5f5',
                  color: selectedOutcome === outcome.id ? 'white' : '#333',
                  border: selectedOutcome === outcome.id ? '2px solid rgba(255, 0, 110, 0.5)' : '2px solid transparent',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  transform: selectedOutcome === outcome.id ? 'scale(1.05)' : 'scale(1)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  boxShadow: selectedOutcome === outcome.id ? '0 4px 15px rgba(255, 0, 110, 0.4)' : 'none'
                }}
              >
                <div>{outcome.label}</div>
                <div style={{ 
                  fontSize: '10px', 
                  fontWeight: 'normal',
                  opacity: selectedOutcome === outcome.id ? 0.9 : 0.7
                }}>
                  {pool} USDC
                </div>
              </button>
            );
          })}
        </div>

        {/* Amount Input */}
        <div style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <label style={{ fontSize: '12px', color: '#666' }}>
              Amount (USDC)
            </label>
            <div style={{ fontSize: '11px', color: '#666' }}>
              Pool Size: <strong>{liveTotalPool ? formatUnits(liveTotalPool, 6) : safePoolData.totalPool} USDC</strong>
            </div>
          </div>
          <input
            type="number"
            value={betAmount}
            onChange={(e) => {
              const value = e.target.value;
              // Allow empty string (for clearing)
              if (value === '') {
                setBetAmount('');
                return;
              }
              // Parse and validate
              const num = parseFloat(value);
              if (!isNaN(num) && num >= 0 && num <= MAX_TRADE_USDC) {
                setBetAmount(value);
              }
            }}
            onBlur={(e) => {
              // On blur, ensure minimum value
              const num = parseFloat(e.target.value);
              if (isNaN(num) || num < MIN_TRADE_USDC) {
                setBetAmount(String(MIN_TRADE_USDC));
              }
            }}
            min={MIN_TRADE_USDC}
            max={MAX_TRADE_USDC}
            step="0.01"
            placeholder="1"
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
          <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
            Balance: {usdcBalance ? formatUnits(usdcBalance, USDC_DECIMALS) : '0'} USDC • Min: {MIN_TRADE_USDC} USDC • Max: {MAX_TRADE_USDC.toLocaleString()} USDC
          </div>
        </div>

        {/* Trade Button */}
        <button
          onClick={handleBet}
          disabled={selectedOutcome === null || !betAmount || isApproving || isBetting || step === 'explain'}
          style={{
            width: '100%',
            padding: '12px',
            background: (selectedOutcome === null || !betAmount || isApproving || isBetting || step === 'explain') ? '#ccc' : 'linear-gradient(135deg, #64ffda 0%, #4fd6b8 100%)',
            color: (selectedOutcome === null || !betAmount || isApproving || isBetting || step === 'explain') ? '#666' : '#0a0e27',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: (selectedOutcome === null || !betAmount || isApproving || isBetting || step === 'explain') ? 'not-allowed' : 'pointer',
            opacity: (isApproving || isBetting || step === 'explain') ? 0.7 : 1,
            boxShadow: (selectedOutcome !== null && betAmount && !isApproving && !isBetting && step !== 'explain') ? '0 4px 20px rgba(100, 255, 218, 0.3)' : 'none'
          }}
        >
          {isApproving ? '⏳ Approving USDC...' : isBetting ? '⏳ Placing Trade...' : 'Trade'}
        </button>
      </div>

      {/* Error Modal */}
      <ErrorModal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ ...errorModal, isOpen: false })}
        title={errorModal.title}
        message={errorModal.message}
        type={errorModal.type}
        detail={errorModal.detail}
        linkUrl={errorModal.linkUrl}
        linkLabel={errorModal.linkLabel}
        onCloseCallback={errorModal.onCloseCallback}
        onConfirm={errorModal.onConfirm}
        confirmLabel={errorModal.confirmLabel}
        cancelLabel={errorModal.cancelLabel}
      />
    </div>
  );
}

export default BettingPanel;

